'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { useDebounce } from '@eliadmin/core/src/hooks/use-debounce';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { fetchFitmentTargets, type FitmentProduct } from '../../../api/product-fitments-api';
import { FitmentPagination, FitmentVariantSummary } from '../../spareparts/fitment-ui';

const RESULTS_PER_PAGE = 30;

function getSourceVariantLabel(product: Product, variantId: number) {
  const variant = product.variants?.find(item => item.id === variantId);
  if (!variant) return `#${variantId}`;

  const values =
    variant.values
      ?.map(value => value.value || value.name)
      .filter((value): value is string => Boolean(value)) ?? [];

  return variant.name || values.join(' / ') || variant.code || `#${variant.id}`;
}

export function ProductFitsOn({ product }: { product: Product }) {
  const { t } = useTranslations();
  const locale = useLocaleStore(state => state.currentLocale);
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sourceVariantId, setSourceVariantId] = useState<number | null>(null);
  const selectedSourceVariant = useVariantsStore(state => state.selectedVariant);
  const sourceVariants = product.variants ?? [];
  const initialUrlVariantId = useRef(
    typeof window === 'undefined'
      ? null
      : Number(new URLSearchParams(window.location.search).get('vid')) || null,
  );
  const hasCapturedAutomaticVariant = useRef(false);
  const lastObservedStoreVariantId = useRef<number | null>(null);
  const debouncedSearch = useDebounce(search.trim(), 300);

  // Core initializes the first available variant automatically. Keep the fitment
  // request product-wide until the URL contained a variant or the customer later
  // changes the product variant explicitly.
  useEffect(() => {
    const currentVariantId = selectedSourceVariant?.id ?? null;
    if (currentVariantId === null || sourceVariants.length <= 1) return;

    if (!hasCapturedAutomaticVariant.current) {
      hasCapturedAutomaticVariant.current = true;
      lastObservedStoreVariantId.current = currentVariantId;

      if (initialUrlVariantId.current === currentVariantId) {
        setSourceVariantId(currentVariantId);
      }
      return;
    }

    if (lastObservedStoreVariantId.current === currentVariantId) return;

    lastObservedStoreVariantId.current = currentVariantId;
    setSourceVariantId(currentVariantId);
  }, [selectedSourceVariant?.id, sourceVariants.length]);

  useEffect(() => {
    setPage(1);
  }, [sourceVariantId]);

  const fitmentsQuery = useQuery({
    queryKey: [
      'rsmoto',
      'fitments',
      'targets',
      product.id,
      sourceVariantId,
      debouncedSearch,
      page,
      locale,
    ],
    queryFn: ({ signal }) =>
      fetchFitmentTargets({
        locale,
        signal,
        query: {
          sourceProductId: product.id,
          sourceVariantId: sourceVariantId ?? undefined,
          search: debouncedSearch || undefined,
          results: RESULTS_PER_PAGE,
          page,
          includeVariants: true,
        },
      }),
  });

  const fittedProducts = fitmentsQuery.data?.data ?? [];

  if (
    !fitmentsQuery.isLoading &&
    !fitmentsQuery.isError &&
    fittedProducts.length === 0 &&
    sourceVariantId === null &&
    debouncedSearch === ''
  ) {
    return null;
  }

  return (
    <Container className="space-y-6 px-0 py-8">
      <div>
        <h2 className="text-2xl font-semibold">Na ktoré motorky diel pasuje</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Kompatibilita je načítaná priamo z produktových fitmentov.
        </p>
      </div>

      {sourceVariantId !== null && (
        <p className="text-sm text-muted-foreground">
          Kompatibilita pre aktuálne vybraný variant dielu:{' '}
          <strong className="text-foreground">
            {getSourceVariantLabel(product, sourceVariantId)}
          </strong>
        </p>
      )}

      {sourceVariants.length > 1 && (
        <div className="max-w-xl space-y-2">
          <Label htmlFor={`fits-on-source-variant-${product.id}`}>
            Variant dielu pre kompatibilitu
          </Label>
          <select
            id={`fits-on-source-variant-${product.id}`}
            className="h-12 w-full rounded-lg border border-border bg-background px-3 text-sm"
            value={sourceVariantId ?? ''}
            onChange={event => {
              setSourceVariantId(event.target.value ? Number(event.target.value) : null);
              setPage(1);
            }}
          >
            <option value="">Všetky varianty dielu</option>
            {sourceVariants.map(variant => (
              <option key={variant.id} value={variant.id}>
                {getSourceVariantLabel(product, variant.id)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="max-w-xl space-y-2">
        <Label htmlFor={`fits-on-search-${product.id}`}>Filtrovať motorky</Label>
        <Input
          id={`fits-on-search-${product.id}`}
          type="search"
          placeholder="Názov alebo kód motorky"
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            setPage(1);
          }}
        />
      </div>

      {fitmentsQuery.isError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm"
        >
          Kompatibilné motorky sa nepodarilo načítať. Skúste to, prosím, znova.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full table-auto border-collapse text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="p-3 text-left">Kód</th>
              <th className="p-3 text-left">Motorka</th>
              <th className="p-3 text-left">Cena</th>
              <th className="p-3 text-right">Detail</th>
            </tr>
          </thead>
          <tbody>
            {fitmentsQuery.isLoading ? (
              Array.from({ length: 5 }, (_, index) => (
                <tr key={index} className="border-b">
                  <td className="p-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-muted" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-52 animate-pulse rounded bg-muted" />
                  </td>
                  <td className="p-3">
                    <div className="h-4 w-20 animate-pulse rounded bg-muted" />
                  </td>
                  <td className="p-3">
                    <div className="ml-auto h-9 w-20 animate-pulse rounded bg-muted" />
                  </td>
                </tr>
              ))
            ) : fittedProducts.length === 0 ? (
              <tr>
                <td colSpan={4} className="p-8 text-center text-muted-foreground">
                  Pre zvolený variant alebo filter sa nenašla kompatibilná motorka.
                </td>
              </tr>
            ) : (
              fittedProducts.map((item: FitmentProduct) => {
                const priceDisplay = getProductPriceDisplay(item);

                return (
                  <tr key={item.id} className="border-b align-top hover:bg-muted/30">
                    <td className="p-3 font-mono text-xs">{item.code || item.ext_code || '-'}</td>
                    <td className="space-y-2 p-3">
                      <strong className="block font-medium">{item.name}</strong>
                      <FitmentVariantSummary product={item} mode="details" />
                    </td>
                    <td className="whitespace-nowrap p-3">
                      {priceDisplay.priceOnRequest
                        ? t('text2278')
                        : priceDisplay.currentWithVat != null
                          ? formatPrice(
                              priceDisplay.currentWithVat,
                              generalLayout.currency,
                              generalLayout.currency_settings,
                            )
                          : '-'}
                    </td>
                    <td className="p-3 text-right">
                      <Button asChild type="button" size="sm" variant="outline">
                        <Link href={`/${item.slug}`}>Detail</Link>
                      </Button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col items-center gap-2">
        {fitmentsQuery.data?.meta && (
          <span className="text-xs text-muted-foreground">
            Zobrazené {fitmentsQuery.data.meta.from || 0}–{fitmentsQuery.data.meta.to || 0} z{' '}
            {fitmentsQuery.data.meta.total}
          </span>
        )}
        <FitmentPagination
          page={page}
          lastPage={fitmentsQuery.data?.meta.last_page ?? 1}
          onPageChange={setPage}
        />
      </div>
    </Container>
  );
}
